import React, { useState, useCallback, useEffect, useMemo } from "react";
import _ from "underscore";
import { useSearchParams } from "react-router-dom";
import styles from "./teacherConsolidatedData.module.css";
import SearchIcon from "../../../assets/icons/search-icon.svg";
import FileExportIcon from "../../../assets/icons/file-export.svg";
import FilterIcon from "../../../assets/icons/bars-filter.svg";
import SavedFiltersIcon from "../../../assets/icons/saved-filters.svg";
import DatabaseIcon from "../../../assets/icons/users.svg";
import ColumnsIcon from "../../../assets/icons/manage-columns.svg";
import { useInstitution } from "../../../context/InstitutionContext";
import { teacherConsolidatedData, getSavedTeacherFilterById } from "../../../services/education";
import { toast } from "react-toastify";
import loadingGif from "../../../assets/loading.gif";
import api from "../../../http/api";
import Button from "../../../components/button/button";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";
import SavedFiltersModal from "../../../components/filters/savedFiltersModal/savedFiltersModal";
import SaveFilterModal from "../../../components/filters/saveFilterModal/saveFilterModal";
import FilterConfigModal from "../../../components/filters/filterConfigModal/filterConfigModal";
import ColumnManagerModal from "../../../components/filters/columnManagerModal/columnManagerModal";

const TeacherConsolidatedData = () => {
  const { institution } = useInstitution();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState("");
  const [consolidatedData, setConsolidatedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(30);
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [pendingFilters, setPendingFilters] = useState([]);
  const [currentFilterField, setCurrentFilterField] = useState("");
  const [currentFilterOperator, setCurrentFilterOperator] = useState("eq");
  const [currentFilterValues, setCurrentFilterValues] = useState([]);
  const [currentFilterValue, setCurrentFilterValue] = useState("");
  const [currentFilterCondition, setCurrentFilterCondition] = useState("AND");
  const [loadingFilterValues, setLoadingFilterValues] = useState(false);
  const [selectedMultipleValues, setSelectedMultipleValues] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [showSavedFiltersModal, setShowSavedFiltersModal] = useState(false);
  const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
  const [showFilterConfigModal, setShowFilterConfigModal] = useState(false);
  const [showColumnManagerModal, setShowColumnManagerModal] = useState(false);

  const [selectedColumns, setSelectedColumns] = useState([
    'teacherId',
    'status',
    'terminationDate',
    'state',
    'city',
    'gender',
    'maxDegree',
    'workRegime',
    'weeklyHoursIES',
    'classroomHoursPerWeek'
  ]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const availableColumns = useMemo(() => [
    { key: 'teacherId', label: 'ID do Docente' },
    { key: 'state', label: 'UF' },
    { key: 'city', label: 'Cidade' },
    { key: 'birthDate', label: 'Data de Nascimento' },
    { key: 'entryDate', label: 'Data de Ingresso' },
    { key: 'terminationDate', label: 'Data de Término' },
    { key: 'status', label: 'Status' },
    { key: 'gender', label: 'Gênero' },
    { key: 'maritalStatus', label: 'Estado Civil' },
    { key: 'maxDegree', label: 'Titulação' },
    { key: 'higherEducationYears', label: 'Anos no Ens. Superior' },
    { key: 'workMarketYears', label: 'Anos no Mercado' },
    { key: 'workRegime', label: 'Regime de Trabalho' },
    { key: 'weeklyHoursIES', label: 'Horas Semanais IES' },
    { key: 'coordinatorYears', label: 'Anos como Coord.' },
    { key: 'classroomHoursPerWeek', label: 'Horas em Sala/Sem.' },
    { key: 'extraClassHoursPerWeek', label: 'Horas Extraclasse/Sem.' },
    { key: 'studentSupportHoursPerWeek', label: 'Horas Atendimento/Sem.' },
    { key: 'isNdeMember', label: 'Membro NDE' },
    { key: 'createdAt', label: 'Data de Criação' },
    { key: 'updatedAt', label: 'Data de Atualização' }
  ], []);

  const filterOperators = useMemo(() => [
    { value: "eq", label: "Igual a (=)" },
    { value: "in", label: "Está em (IN)" },
    { value: "gt", label: "Maior que (>)" },
    { value: "lt", label: "Menor que (<)" },
    { value: "gte", label: "Maior ou igual (>=)" },
    { value: "lte", label: "Menor ou igual (<=)" }
  ], []);

  const filterFields = useMemo(() => [
    { value: "TeacherId", label: "ID do Docente", apiField: "TeacherId" },
    { value: "State", label: "UF", apiField: "State" },
    { value: "City", label: "Cidade", apiField: "City" },
    { value: "Gender", label: "Gênero", apiField: "Gender" },
    { value: "MaritalStatus", label: "Estado Civil", apiField: "MaritalStatus" },
    { value: "MaxDegree", label: "Titulação", apiField: "MaxDegree" },
    { value: "WorkRegime", label: "Regime de Trabalho", apiField: "WorkRegime" },
    { value: "WeeklyHoursIES", label: "Horas Semanais IES", apiField: "WeeklyHoursIES" },
    { value: "HigherEducationYears", label: "Anos no Ens. Superior", apiField: "HigherEducationYears" },
    { value: "WorkMarketYears", label: "Anos no Mercado", apiField: "WorkMarketYears" },
    { value: "IsNdeMember", label: "Membro do NDE", apiField: "IsNdeMember" },
    { value: "Status", label: "Status", apiField: "Status" }
  ], []);

  const fetchConsolidatedData = useCallback((pageNumber = 1, term = "", filters = []) => {
    const debouncedFetch = _.debounce(async () => {
      try {
        setLoading(true);

        const params = {
          institutionId: institution.id,
          page: pageNumber,
          pageSize: itemsPerPage,
          searchTerm: term,
          filters
        };

        const response = await teacherConsolidatedData(params);

        setLastApiResponse(response.teacherData);
        setConsolidatedData(response.teacherData.teacherConsolidatedData);
        setPaginationInfo({
          pageNumber: response.teacherData.currentPage,
          pageSize: itemsPerPage,
          totalCount: response.teacherData.totalResults,
          totalPages: response.teacherData.totalPages,
          hasPreviousPage: response.teacherData.currentPage > 1,
          hasNextPage: response.teacherData.currentPage < response.teacherData.totalPages
        });
      } catch (error) {
        console.error("Erro ao buscar dados consolidados de docentes:", error);
        setConsolidatedData([]);
        setPaginationInfo(null);
      } finally {
        setLoading(false);
      }
    }, 1000);

    debouncedFetch();
  }, [institution?.id, itemsPerPage]);

  const fetchFilterValues = useCallback(async (fieldName) => {
    if (!institution?.id || !fieldName) return;

    try {
      setLoadingFilterValues(true);

      const fieldMapping = filterFields.find(f => f.value === fieldName);
      const apiFieldName = fieldMapping ? fieldMapping.apiField : fieldName;

      const response = await api.client.get(
        `api/v1/TeacherInstitution/FilterOptions`,
        {
          params: { fieldName: apiFieldName },
          headers: { institutionId: institution.id }
        }
      );

      setCurrentFilterValues(response.data?.options || []);
    } catch (error) {
      console.error("Erro ao buscar valores do filtro:", error);
      setCurrentFilterValues([]);
    } finally {
      setLoadingFilterValues(false);
    }
  }, [institution?.id, filterFields]);

  const updateURL = (filters, filterId = null) => {
    const newSearchParams = new URLSearchParams();
    if (searchTerm) newSearchParams.set('search', searchTerm);
    newSearchParams.set('page', currentPage.toString());

    if (filterId) {
      newSearchParams.set('filterId', filterId);
    } else {
      filters.forEach((filter, index) => {
        newSearchParams.set(`filter${index}_field`, filter.field);
        newSearchParams.set(`filter${index}_operator`, filter.operator || 'eq');
        newSearchParams.set(`filter${index}_values`, JSON.stringify(filter.values || []));
        newSearchParams.set(`filter${index}_groupId`, filter.groupId || 0);
        if (filter.condition) newSearchParams.set(`filter${index}_condition`, filter.condition);
      });
    }

    setSearchParams(newSearchParams);
  };

  const handleFilterFieldChange = (field) => {
    setCurrentFilterField(field);
    setCurrentFilterOperator("eq");
    setCurrentFilterValue("");
    setCurrentFilterValues([]);
    setSelectedMultipleValues([]);
    if (field) fetchFilterValues(field);
  };

  const handleOperatorChange = (operator) => {
    setCurrentFilterOperator(operator);
    setCurrentFilterValue("");
    setSelectedMultipleValues([]);
    if ((operator === "eq" || operator === "in") && currentFilterField && currentFilterValues.length === 0) {
      fetchFilterValues(currentFilterField);
    }
  };

  const handleAddFilter = () => {
    if (!currentFilterField) return;
    if (currentFilterOperator === "in" && selectedMultipleValues.length === 0) return;
    if (currentFilterOperator !== "in" && !currentFilterValue) return;

    const fieldMapping = filterFields.find(f => f.value === currentFilterField);
    const apiFieldName = fieldMapping ? fieldMapping.apiField : currentFilterField;
    const fieldLabel = fieldMapping ? fieldMapping.label : currentFilterField;
    const operatorMapping = filterOperators.find(op => op.value === currentFilterOperator);
    const operatorLabel = operatorMapping ? operatorMapping.label : currentFilterOperator;

    let groupId = 0;
    if (pendingFilters.length > 0) {
      const lastFilter = pendingFilters[pendingFilters.length - 1];
      groupId = currentFilterCondition === "OR" ? lastFilter.groupId + 1 : lastFilter.groupId;
    }

    const filterValues = currentFilterOperator === "in" ? selectedMultipleValues : [currentFilterValue];
    const displayValue = currentFilterOperator === "in" ? selectedMultipleValues.join(", ") : currentFilterValue;

    const newFilter = {
      field: apiFieldName,
      operator: currentFilterOperator,
      values: filterValues,
      label: fieldLabel,
      operatorLabel,
      displayValue,
      condition: pendingFilters.length > 0 ? currentFilterCondition : null,
      groupId
    };

    setPendingFilters([...pendingFilters, newFilter]);
    setCurrentFilterField("");
    setCurrentFilterOperator("eq");
    setCurrentFilterValue("");
    setCurrentFilterValues([]);
    setSelectedMultipleValues([]);
    setCurrentFilterCondition("AND");
  };

  const handleRemoveFilter = (index) => {
    setPendingFilters(pendingFilters.filter((_, i) => i !== index));
  };

  const handleClearAllFilters = () => {
    setPendingFilters([]);
    setAppliedFilters([]);
    setCurrentFilterField("");
    setCurrentFilterOperator("eq");
    setCurrentFilterValue("");
    setCurrentFilterValues([]);
    setSelectedMultipleValues([]);
    setCurrentFilterCondition("AND");
    setCurrentPage(1);
    updateURL([]);
    fetchConsolidatedData(1, searchTerm, []);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(pendingFilters);
    setCurrentPage(1);
    updateURL(pendingFilters);
    fetchConsolidatedData(1, searchTerm, pendingFilters);
  };

  const handleApplyFiltersFromModal = (filters) => {
    const formattedFilters = filters.map((filter, index) => {
      const fieldMapping = filterFields.find(f => f.value === filter.field);
      const operatorMapping = filterOperators.find(op => op.value === filter.operator);

      let groupId = 0;
      if (index > 0) {
        const prevFilter = formattedFilters[index - 1];
        groupId = filter.condition === 'OR' ? (prevFilter.groupId + 1) : prevFilter.groupId;
      }

      return {
        field: fieldMapping ? fieldMapping.apiField : filter.field,
        operator: filter.operator,
        values: filter.values || [filter.value],
        label: fieldMapping ? fieldMapping.label : filter.field,
        operatorLabel: operatorMapping ? operatorMapping.label : filter.operator,
        displayValue: Array.isArray(filter.values) ? filter.values.join(', ') : filter.value,
        condition: filter.condition || null,
        groupId
      };
    });

    setPendingFilters(formattedFilters);
    setAppliedFilters(formattedFilters);
    setCurrentPage(1);
    updateURL(formattedFilters);
    fetchConsolidatedData(1, searchTerm, formattedFilters);
  };

  const convertFiltersToApiFormat = () => {
    if (pendingFilters.length === 0) return null;

    const groupedFilters = {};
    pendingFilters.forEach((filter) => {
      const groupId = filter.groupId !== undefined ? filter.groupId : 0;
      if (!groupedFilters[groupId]) groupedFilters[groupId] = [];
      groupedFilters[groupId].push(filter);
    });

    const filterGroups = Object.values(groupedFilters).map((group) => ({
      condition: "AND",
      filters: group.map((filter) => ({
        field: filter.field,
        operator: filter.operator === "eq" ? "=" : filter.operator,
        values: filter.values
      }))
    }));

    return {
      page: 1,
      pageSize: itemsPerPage,
      groupCondition: "AND",
      filterGroups,
      source: "consolidadosDocentes"
    };
  };

  const convertApiFilterToLocal = useCallback((savedFilter) => {
    try {
      let filterData;
      if (typeof savedFilter.filterJson === 'string') {
        filterData = JSON.parse(savedFilter.filterJson);
      } else if (savedFilter.filter) {
        filterData = savedFilter.filter;
      } else {
        filterData = savedFilter.filterJson;
      }

      if (!filterData?.filterGroups || filterData.filterGroups.length === 0) return [];

      const localFilters = [];
      let currentGroupId = 0;

      filterData.filterGroups.forEach((group, groupIndex) => {
        group.filters.forEach((filter, filterIndex) => {
          const fieldMapping = filterFields.find(f => f.apiField === filter.field);
          const operator = filter.operator === "=" ? "eq" : filter.operator;
          const operatorMapping = filterOperators.find(op => op.value === operator);
          const displayValue = Array.isArray(filter.values) ? filter.values.join(", ") : filter.values[0];

          localFilters.push({
            field: filter.field,
            operator,
            values: filter.values,
            label: fieldMapping ? fieldMapping.label : filter.field,
            operatorLabel: operatorMapping ? operatorMapping.label : operator,
            displayValue,
            condition: groupIndex > 0 && filterIndex === 0 ? "OR" : null,
            groupId: currentGroupId
          });
        });
        currentGroupId++;
      });

      return localFilters;
    } catch (error) {
      console.error("Erro ao converter filtro salvo:", error);
      toast.error("Erro ao processar filtro salvo");
      return [];
    }
  }, [filterFields, filterOperators]);

  const handleApplySavedFilter = (savedFilter) => {
    const convertedFilters = convertApiFilterToLocal(savedFilter);

    if (convertedFilters.length === 0) {
      toast.warning("Nenhum filtro encontrado para aplicar");
      return;
    }

    setPendingFilters(convertedFilters);
    setAppliedFilters(convertedFilters);
    setCurrentPage(1);
    updateURL(convertedFilters, savedFilter.id);
    fetchConsolidatedData(1, searchTerm, convertedFilters);
    toast.success(`Filtro "${savedFilter.name}" aplicado com sucesso!`);
  };

  const processedConsolidatedData = consolidatedData ? consolidatedData.map((item) => ({
    teacherId: item.TeacherId || item.teacherId || '-',
    state: item.State || item.state || '-',
    city: item.City || item.city || '-',
    birthDate: formatDate(item.BirthDate || item.birthDate),
    entryDate: formatDate(item.EntryDate || item.entryDate),
    terminationDate: formatDate(item.TerminationDate || item.terminationDate),
    status: item.Status || item.status || '-',
    gender: item.Gender || item.gender || '-',
    maritalStatus: item.MaritalStatus || item.maritalStatus || '-',
    maxDegree: item.MaxDegree || item.maxDegree || '-',
    higherEducationYears: item.HigherEducationYears ?? item.higherEducationYears ?? '-',
    workMarketYears: item.WorkMarketYears ?? item.workMarketYears ?? '-',
    workRegime: item.WorkRegime || item.workRegime || '-',
    weeklyHoursIES: item.WeeklyHoursIES ?? item.weeklyHoursIES ?? '-',
    coordinatorYears: item.CoordinatorYears ?? item.coordinatorYears ?? '-',
    classroomHoursPerWeek: item.ClassroomHoursPerWeek ?? item.classroomHoursPerWeek ?? '-',
    extraClassHoursPerWeek: item.ExtraClassHoursPerWeek ?? item.extraClassHoursPerWeek ?? '-',
    studentSupportHoursPerWeek: item.StudentSupportHoursPerWeek ?? item.studentSupportHoursPerWeek ?? '-',
    isNdeMember: (item.IsNdeMember ?? item.isNdeMember) === true ? 'Sim' : (item.IsNdeMember ?? item.isNdeMember) === false ? 'Não' : '-',
    createdAt: formatDateTime(item.CreatedAt || item.createdAt),
    updatedAt: formatDateTime(item.UpdatedAt || item.updatedAt)
  })) : [];

  const visibleColumns = useMemo(() => {
    return availableColumns
      .filter(col => selectedColumns.includes(col.key))
      .map(col => col.key);
  }, [availableColumns, selectedColumns]);

  const getColumnLabel = (columnKey) => {
    const column = availableColumns.find(col => col.key === columnKey);
    return column ? column.label : columnKey;
  };

  const renderCellValue = (item, columnKey) => {
    return item[columnKey] !== undefined && item[columnKey] !== null ? item[columnKey] : '-';
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
    fetchConsolidatedData(page, searchTerm, appliedFilters);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    fetchConsolidatedData(1, term, appliedFilters);
  };

  const getVisiblePages = () => {
    if (!paginationInfo) return [];

    const totalPages = paginationInfo.totalPages;
    const current = currentPage;
    let visiblePages = [];

    if (totalPages <= 7) {
      visiblePages = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      if (current <= 4) {
        visiblePages = [1, 2, 3, 4, 5, '...', totalPages];
      } else if (current >= totalPages - 3) {
        visiblePages = [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
      } else {
        visiblePages = [1, '...', current - 1, current, current + 1, '...', totalPages];
      }
    }

    return visiblePages;
  };

  const handleExportCsv = async () => {
    if (!lastApiResponse?.executedQuery) {
      toast.error("Nenhuma consulta disponível para exportação");
      return;
    }

    try {
      setExporting(true);
      toast.info("Preparando exportação...");

      const exportQuery = lastApiResponse.executedQuery
        .replace(/OFFSET @Offset ROWS\s*FETCH NEXT @PageSize ROWS ONLY/gi, '');

      const columnToApiFieldMap = {
        teacherId: 'TeacherId',
        state: 'State',
        city: 'City',
        birthDate: 'BirthDate',
        entryDate: 'EntryDate',
        terminationDate: 'TerminationDate',
        status: 'Status',
        gender: 'Gender',
        maritalStatus: 'MaritalStatus',
        maxDegree: 'MaxDegree',
        higherEducationYears: 'HigherEducationYears',
        workMarketYears: 'WorkMarketYears',
        workRegime: 'WorkRegime',
        weeklyHoursIES: 'WeeklyHoursIES',
        coordinatorYears: 'CoordinatorYears',
        classroomHoursPerWeek: 'ClassroomHoursPerWeek',
        extraClassHoursPerWeek: 'ExtraClassHoursPerWeek',
        studentSupportHoursPerWeek: 'StudentSupportHoursPerWeek',
        isNdeMember: 'IsNdeMember',
        createdAt: 'CreatedAt',
        updatedAt: 'UpdatedAt'
      };

      const dynamicHeaders = {};
      visibleColumns.forEach(columnKey => {
        const apiField = columnToApiFieldMap[columnKey];
        if (apiField) {
          const column = availableColumns.find(col => col.key === columnKey);
          if (column) dynamicHeaders[apiField] = column.label;
        }
      });

      const exportPayload = {
        executedQuery: exportQuery,
        parameters: { InstitutionId: institution.id },
        headers: dynamicHeaders,
        fileName: "dados_docentes_consolidados.csv",
        includeHeaders: true,
        delimiter: ";",
        culture: "pt-BR"
      };

      if (searchTerm) exportPayload.parameters.SearchTerm = searchTerm;

      appliedFilters.forEach((filter, index) => {
        if (filter.field && filter.values && filter.values.length > 0) {
          if (filter.operator === 'in' && filter.values.length > 1) {
            exportPayload.parameters[`Filter${index}`] = filter.values;
          } else {
            exportPayload.parameters[`Filter${index}`] = filter.values[0];
          }
        }
      });

      const response = await api.client.post(
        'api/v1/CsvExport/export-from-query',
        exportPayload,
        {
          responseType: 'blob',
          headers: { 'Content-Type': 'application/json', institutionId: institution.id }
        }
      );

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', exportPayload.fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("CSV exportado com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar CSV:", error);
      toast.error("Erro ao exportar dados. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const urlSearch = searchParams.get('search') || "";
    const urlPage = parseInt(searchParams.get('page')) || 1;
    const urlFilterId = searchParams.get('filterId');

    setSearchTerm(urlSearch);
    setCurrentPage(urlPage);

    if (urlFilterId && institution?.id) {
      const fetchFilterById = async () => {
        try {
          setLoading(true);
          const savedFilter = await getSavedTeacherFilterById({
            institutionId: institution.id,
            filterId: urlFilterId
          });
          const convertedFilters = convertApiFilterToLocal(savedFilter);
          setAppliedFilters(convertedFilters);
          setPendingFilters(convertedFilters);
          setInitialLoadComplete(true);
        } catch (error) {
          console.error("Erro ao buscar filtro por ID:", error);
          toast.error("Erro ao carregar filtro da URL");
          setInitialLoadComplete(true);
        } finally {
          setLoading(false);
        }
      };
      fetchFilterById();
    } else {
      const urlFilters = [];
      let filterIndex = 0;
      while (searchParams.get(`filter${filterIndex}_field`)) {
        const field = searchParams.get(`filter${filterIndex}_field`);
        const operator = searchParams.get(`filter${filterIndex}_operator`) || 'eq';
        const valuesStr = searchParams.get(`filter${filterIndex}_values`);
        const condition = searchParams.get(`filter${filterIndex}_condition`);
        const groupId = searchParams.get(`filter${filterIndex}_groupId`) || filterIndex;

        if (field && valuesStr) {
          try {
            const values = JSON.parse(valuesStr);
            const fieldMapping = filterFields.find(f => f.apiField === field);
            const operatorMapping = filterOperators.find(op => op.value === operator);

            urlFilters.push({
              field,
              operator,
              values,
              label: fieldMapping ? fieldMapping.label : field,
              operatorLabel: operatorMapping ? operatorMapping.label : operator,
              displayValue: Array.isArray(values) ? values.join(', ') : values[0],
              condition: condition || null,
              groupId: parseInt(groupId)
            });
          } catch (e) {
            console.error("Erro ao fazer parse dos valores do filtro:", e);
          }
        }
        filterIndex++;
      }

      setAppliedFilters(urlFilters);
      setPendingFilters(urlFilters);
      setInitialLoadComplete(true);
    }
  }, [searchParams, filterFields, filterOperators, institution?.id, convertApiFilterToLocal]);

  useEffect(() => {
    if (institution?.id && initialLoadComplete) {
      fetchConsolidatedData(currentPage, searchTerm, appliedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institution?.id, fetchConsolidatedData, currentPage, searchTerm, appliedFilters, initialLoadComplete]);

  return (
    <div className={styles.consolidatedDataContent}>
      <HeaderTitle title="Dados Consolidados de Docentes" icon={DatabaseIcon}>
        <div className={styles.headerActions}>
          <Button
            icon={FilterIcon}
            text="Filtrar"
            size="normal"
            variation="outline"
            onClick={() => setShowFilterConfigModal(true)}
          />
          <Button
            icon={SavedFiltersIcon}
            text="Filtros Salvos"
            size="normal"
            variation="outline"
            onClick={() => setShowSavedFiltersModal(true)}
          />
          <Button
            icon={ColumnsIcon}
            text="Gerenciar Colunas"
            size="normal"
            variation="outline"
            onClick={() => setShowColumnManagerModal(true)}
          />
        </div>
      </HeaderTitle>

      <div className={styles.consolidatedDataBody}>
        <div className={styles.tableContainer}>

          {appliedFilters.length > 0 && (
            <div className={styles.appliedFiltersSection}>
              <span className={styles.appliedFiltersLabel}>Filtros aplicados:</span>
              <div className={styles.appliedFiltersList}>
                {appliedFilters.map((filter, index) => (
                  <div key={index} className={styles.appliedFilterChip}>
                    <span className={styles.appliedFilterField}>{filter.label}</span>
                    <span className={styles.appliedFilterOperator}>{filter.operator === 'eq' ? 'IGUAL A (=)' : filter.operatorLabel}</span>
                    <span className={styles.appliedFilterValue}>{filter.displayValue}</span>
                    <button
                      className={styles.removeFilterBtn}
                      onClick={() => {
                        const newFilters = appliedFilters.filter((_, i) => i !== index);
                        setAppliedFilters(newFilters);
                        setPendingFilters(newFilters);
                        setCurrentPage(1);
                        updateURL(newFilters);
                        fetchConsolidatedData(1, searchTerm, newFilters);
                      }}
                      title="Remover filtro"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  className={styles.clearFiltersBtn}
                  onClick={handleClearAllFilters}
                  title="Limpar todos os filtros"
                >
                  × Limpar Todos
                </button>
              </div>
            </div>
          )}

          <div className={styles.tableActions}>
            <div className={styles.searchInputWrapper}>
              <img src={SearchIcon} alt="Pesquisar" className={styles.searchIconTable} />
              <input
                type="text"
                placeholder="Pesquisar..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className={styles.searchInputTable}
              />
            </div>
            <button
              className={styles.exportCsvButton}
              onClick={handleExportCsv}
              disabled={exporting}
            >
              {exporting ? (
                <>
                  <img src={loadingGif} alt="Exportando..." className={styles.exportIconTable} />
                  Exportando...
                </>
              ) : (
                <>
                  <img src={FileExportIcon} alt="Exportar" className={styles.exportIconTable} />
                  Exportar CSV
                </>
              )}
            </button>
          </div>

          <div className={styles.tableWrapper}>
            <table className={styles.consolidatedDataTable}>
              <thead>
                <tr>
                  {visibleColumns.map(columnKey => (
                    <th key={columnKey}>{getColumnLabel(columnKey)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={visibleColumns.length} style={{ textAlign: 'center', padding: '40px' }}>
                      <img src={loadingGif} alt="Carregando..." className={styles.loadingIcon} />
                      <p>Carregando dados de docentes...</p>
                    </td>
                  </tr>
                ) : processedConsolidatedData.length > 0 ? (
                  processedConsolidatedData.map((item, index) => (
                    <tr key={`${item.teacherId}-${index}`}>
                      {visibleColumns.map(columnKey => (
                        <td key={columnKey}>
                          {renderCellValue(item, columnKey)}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={visibleColumns.length} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Nenhum dado de docente encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className={styles.pagination}>
            {paginationInfo && paginationInfo.totalPages > 1 && (
              <>
                <button
                  className={styles.paginationArrow}
                  onClick={() => handlePageClick(currentPage - 1)}
                  disabled={currentPage === 1}
                  title="Página anterior"
                >
                  ←
                </button>

                {getVisiblePages().map((page, index) => (
                  <button
                    key={`page-${page}-${index}`}
                    className={
                      page === '...'
                        ? styles.paginationDots
                        : page === currentPage
                          ? styles.activePage
                          : styles.paginationButton
                    }
                    onClick={() => page !== '...' && handlePageClick(page)}
                    disabled={page === '...'}
                  >
                    {page}
                  </button>
                ))}

                <button
                  className={styles.paginationArrow}
                  onClick={() => handlePageClick(currentPage + 1)}
                  disabled={currentPage === paginationInfo.totalPages}
                  title="Próxima página"
                >
                  →
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {showFilterConfigModal && (
        <FilterConfigModal
          onClose={() => setShowFilterConfigModal(false)}
          onApply={handleApplyFiltersFromModal}
          filterFields={filterFields}
          filterOperators={filterOperators}
          institutionId={institution?.id}
          filterOptionsEndpoint="api/v1/TeacherInstitution/FilterOptions"
        />
      )}

      {showSavedFiltersModal && (
        <SavedFiltersModal
          onClose={() => setShowSavedFiltersModal(false)}
          onApplyFilter={handleApplySavedFilter}
          filterSource="consolidadosDocentes"
          onCreateFilter={() => setShowSaveFilterModal(true)}
        />
      )}

      {showSaveFilterModal && (
        <SaveFilterModal
          onClose={() => setShowSaveFilterModal(false)}
          onFilterSaved={() => toast.success("Filtro salvo!")}
          filterData={convertFiltersToApiFormat()}
        />
      )}

      {showColumnManagerModal && (
        <ColumnManagerModal
          onClose={() => setShowColumnManagerModal(false)}
          onApply={(columns) => {
            setSelectedColumns(columns);
            toast.success("Colunas atualizadas!");
          }}
          availableColumns={availableColumns}
          selectedColumns={selectedColumns}
          defaultColumns={[
            'teacherId',
            'state',
            'city',
            'gender',
            'maxDegree',
            'workRegime',
            'weeklyHoursIES',
            'classroomHoursPerWeek'
          ]}
        />
      )}
    </div>
  );
};

export default TeacherConsolidatedData;
